<script setup lang="ts">
import { computed, ref } from 'vue';
import { Article } from '/@types/articles';

/**
 * Used for creating additional registrations. Only for articles with CanAddAdditionalRegistrations set to true from admin page. Only allowed to add "Tillegg"
 */

type Registration = { articleId: number; count: number };

const props = defineProps<{
  allRegistrations: Map<number, Article>;
  modelValue: Registration[];
}>();

const articleOptions = computed(() => {
  const option = new Map<number, Article>();

  props.allRegistrations.forEach(
    (article) => article.articleTypeId === 200 && option.set(article.id, article),
  );
  return option;
});

const model = defineModel<Registration[]>({ default: [] });

const isModalOpen = ref(false);

const newRegistration = ref<Registration | null>(null);

function setRegistration(articleId: number) {
  newRegistration.value = { articleId, count: 1 };
}

function addRegistration() {
  const r = newRegistration.value;
  if (!r) return;

  // handle multiple of same article
  const idx = model.value.findIndex((x) => x.articleId === r.articleId);
  if (idx >= 0) {
    const copy = [...model.value];
    copy[idx] = { ...copy[idx], count: copy[idx].count + r.count };
    model.value = copy;
  } else {
    model.value = [...model.value, r];
  }

  newRegistration.value = null;
  isModalOpen.value = false;
}

function removeRegistration(articleId: number) {
  model.value = model.value.filter((r) => r.articleId !== articleId);
}

function closeModal() {
  isModalOpen.value = false;
  newRegistration.value = null;
}
</script>
<template>
  <div v-if="model.length > 0">
    <div class="title-sm">Valgte tillegg</div>

    <ul class="additional-registration-list">
      <li
        v-for="registration in model"
        :key="registration.articleId"
        class="additional-registration-list-item"
      >
        <div class="name">{{ allRegistrations.get(registration.articleId)?.name }}</div>
        <div class="count">{{ registration.count }}</div>

        <df-button @click="removeRegistration(registration.articleId)">Fjern</df-button>
      </li>
    </ul>
  </div>

  <div class="button-container">
    <df-button @click="isModalOpen = true"> Legg til tillegg </df-button>
  </div>

  <DfModal v-if="isModalOpen">
    <div class="modal-content">
      <header class="head">
        <df-button class="head__back" @click="closeModal()">
          <template v-slot:icon>
            <df-icon code="f060" />
          </template>
        </df-button>

        <df-header :close="true" @click="closeModal()">Legge til tillegg</df-header>
      </header>

      <DfSelect>
        <template v-slot:text>
          {{
            newRegistration?.articleId
              ? articleOptions.get(newRegistration.articleId)?.name
              : 'Velg tillegg'
          }}
        </template>

        <df-dropdown2-item
          v-for="[id, article] in articleOptions"
          :key="article.id"
          @click="setRegistration(id)"
        >
          {{ article.name }}
        </df-dropdown2-item>
      </DfSelect>

      <df-counter
        v-if="newRegistration?.articleId"
        v-model="newRegistration.count"
        :step="1"
        :min-value="1"
        label="Antall"
        elevate
      />
      <div class="button-container">
        <df-button v-if="!!newRegistration?.articleId" @click="addRegistration()">
          Legg til
        </df-button>
      </div>
    </div>
  </DfModal>
</template>
<style scoped>
.modal-content {
  display: grid;
  gap: var(--gap-lg);
  padding: var(--gap-lg);
}

.additional-registration-list {
  margin-top: var(--gap-md);
  padding: 0 0.5rem;

  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);

  background-color: var(--color-cardbg);
}

.additional-registration-list-item {
  display: flex;
  align-items: center;
  gap: 1rem;

  padding: var(--gap-md) 0;

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-divider);
  }

  .name {
    flex: 1;
  }

  .count {
    min-width: 2rem;
    text-align: right;
  }
}

.button-container {
  display: flex;
  justify-content: end;
}
</style>
